import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../utils/config';

function UserAdditionalInfoModal({ isOpen, onClose, userId, isAdminView = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    patient_id: userId || '',
    middle_name: '',
    city: '',
    province_or_state: '',
    postal_zip_code: '',
    country: '',
    primary_language: '',
    reside_with_family_member: false,
    height: '',
    weight: '',
    any_healthcare_program: '',
    insurance_benefit: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    health_card_id: '',
    primary_physician_name: '',
    primary_physician_phone: '',
    primary_physician_address: '',
    health_condition: '',
    mobility: '',
    meal_alert: '',
    allergies: '',
    medication_list: '',
    dietary_restrictions: '',
    hospital_history: '',
    physical_limitation: '',
    mental_health_issue: '',
    level_of_care_needed: '',
    daily_care_activities: '',
    special_care_instructions: '',
    schedule_psw_visit: '',
    notes: '',
    client_preferred_communication_method: '',
    preferred_psw: '',
    cultural_religious_preferences: '',
    emergency_procedures: '',
    known_triggers_for_medical_emergencies: '',
    service_start_date: '',
    service_end_date: '',
    caregiver_assigned: '',
    insurer_name: '',
    billing_address: '',
    payment_plan: '',
    payment_due_date: '',
    billing_contact_name: '',
    billing_contact_phone: ''
  });

  useEffect(() => {
    if (isOpen && userId) {
      fetchAdditionalInfo();
    }
  }, [isOpen, userId]);

  const fetchAdditionalInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let mergedData = { ...formData };
      
      // First, fetch user details from /admin/user/{userId}
      if (userId) {
        try {
          const userRes = await fetch(`${BACKEND_URL}/admin/user/${userId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });

          if (userRes.ok) {
            const userDetailsData = await userRes.json();
            mergedData = { ...mergedData, ...(userDetailsData.user || userDetailsData) };
          }
        } catch (err) {
          console.error('Error fetching user details from /admin/user endpoint:', err);
        }
      }
      
      // Then, attempt to fetch fresh data from /admin/profile
      try {
        let profileResponse;
        
        if (isAdminView) {
          // Admin viewing user's additional info
          profileResponse = await fetch(`${BACKEND_URL}/admin/profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              id: userId,
              user_type: 'patient'
            })
          });
        } else {
          // User viewing their own additional info
          profileResponse = await fetch(`${BACKEND_URL}/users/profile`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
        }

        if (profileResponse.ok) {
          try {
            const profileData = await profileResponse.json();
            mergedData = { ...mergedData, ...(profileData.user || profileData) };
          } catch (jsonError) {
            console.error('Error parsing JSON:', jsonError);
          }
        }
      } catch (err) {
        console.error('Error fetching profile info:', err);
      }
      
      setFormData(mergedData);
    } catch (err) {
      console.error('Error fetching additional info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Helper function to format date to ISO 8601 with UTC timezone
      const formatDateToISO = (dateStr) => {
        if (!dateStr) return null;
        return `${dateStr}T00:00:00Z`;
      };

      // Build payload with only fields matching the Rust struct and proper types
      const payload = {
        patient_id: formData.patient_id,
        middle_name: formData.middle_name || null,
        city: formData.city || null,
        province_or_state: formData.province_or_state || null,
        postal_zip_code: formData.postal_zip_code || null,
        country: formData.country || null,
        primary_language: formData.primary_language || null,
        reside_with_family_member: formData.reside_with_family_member || null,
        height: formData.height ? parseInt(formData.height, 10) : null,
        weight: formData.weight ? parseInt(formData.weight, 10) : null,
        any_healthcare_program: formData.any_healthcare_program || null,
        insurance_benefit: formData.insurance_benefit || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_relationship: formData.emergency_contact_relationship || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        health_card_id: formData.health_card_id || null,
        primary_physician_name: formData.primary_physician_name || null,
        primary_physician_phone: formData.primary_physician_phone || null,
        primary_physician_address: formData.primary_physician_address || null,
        health_condition: formData.health_condition || null,
        mobility: formData.mobility || null,
        meal_alert: formData.meal_alert || null,
        allergies: formData.allergies || null,
        medication_list: formData.medication_list || null,
        dietary_restrictions: formData.dietary_restrictions || null,
        hospital_history: formData.hospital_history || null,
        physical_limitation: formData.physical_limitation || null,
        mental_health_issue: formData.mental_health_issue || null,
        level_of_care_needed: formData.level_of_care_needed || null,
        daily_care_activities: formData.daily_care_activities || null,
        special_care_instructions: formData.special_care_instructions || null,
        schedule_psw_visit: formData.schedule_psw_visit || null,
        notes: formData.notes || null,
        client_preferred_communication_method: formData.client_preferred_communication_method || null,
        preferred_psw: formData.preferred_psw || null,
        cultural_religious_preferences: formData.cultural_religious_preferences || null,
        emergency_procedures: formData.emergency_procedures || null,
        known_triggers_for_medical_emergencies: formData.known_triggers_for_medical_emergencies || null,
        service_start_date: formatDateToISO(formData.service_start_date),
        service_end_date: formatDateToISO(formData.service_end_date),
        caregiver_assigned: formData.caregiver_assigned || null,
        insurer_name: formData.insurer_name || null,
        billing_address: formData.billing_address || null,
        payment_plan: formData.payment_plan || null,
        payment_due_date: formData.payment_due_date || null,
        billing_contact_name: formData.billing_contact_name || null,
        billing_contact_phone: formData.billing_contact_phone || null,
      };

      const response = await fetch(`${BACKEND_URL}/users/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save additional information');
      }

      try {
        await response.json();
        setSuccess('Additional information saved successfully!');
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (jsonError) {
        console.error('Error parsing JSON:', jsonError);
        throw new Error('Received an invalid response from the server.');
      }
    } catch (err) {
      setError(err.message || 'Failed to save information');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Additional Information</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        {error && (
          <div style={{ color: '#ef4444', padding: '12px', background: '#fee', borderRadius: '4px', margin: '12px 20px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ color: '#10b981', padding: '12px', background: '#d1fae5', borderRadius: '4px', margin: '12px 20px' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto', padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            
            {/* Personal Information Section */}
            <div style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Personal Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="middle_name">Middle Name</label>
              <input
                type="text"
                id="middle_name"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="country">Country</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="primary_language">Primary Language</label>
              <input
                type="text"
                id="primary_language"
                name="primary_language"
                value={formData.primary_language}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Address Information Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Address Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="province_or_state">Province/State</label>
              <input
                type="text"
                id="province_or_state"
                name="province_or_state"
                value={formData.province_or_state}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="postal_zip_code">Postal/Zip Code</label>
              <input
                type="text"
                id="postal_zip_code"
                name="postal_zip_code"
                value={formData.postal_zip_code}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Physical Information Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Physical Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="height">Height (cm)</label>
              <input
                type="number"
                id="height"
                name="height"
                value={formData.height}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="weight">Weight (kg)</label>
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobility">Mobility</label>
              <input
                type="text"
                id="mobility"
                name="mobility"
                value={formData.mobility}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Full mobility, Limited, Wheelchair"
              />
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
              <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="reside_with_family_member"
                  checked={formData.reside_with_family_member}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Reside with Family Member
              </label>
            </div>

            {/* Insurance & Healthcare Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Insurance & Healthcare</h3>
            </div>

            <div className="form-group">
              <label htmlFor="health_card_id">Health Card ID</label>
              <input
                type="text"
                id="health_card_id"
                name="health_card_id"
                value={formData.health_card_id}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="any_healthcare_program">Healthcare Program</label>
              <input
                type="text"
                id="any_healthcare_program"
                name="any_healthcare_program"
                value={formData.any_healthcare_program}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="insurance_benefit">Insurance Benefit</label>
              <input
                type="text"
                id="insurance_benefit"
                name="insurance_benefit"
                value={formData.insurance_benefit}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="insurer_name">Insurer Name</label>
              <input
                type="text"
                id="insurer_name"
                name="insurer_name"
                value={formData.insurer_name}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Emergency Contact Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Emergency Contact</h3>
            </div>

            <div className="form-group">
              <label htmlFor="emergency_contact_name">Emergency Contact Name</label>
              <input
                type="text"
                id="emergency_contact_name"
                name="emergency_contact_name"
                value={formData.emergency_contact_name}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="emergency_contact_relationship">Emergency Contact Relationship</label>
              <input
                type="text"
                id="emergency_contact_relationship"
                name="emergency_contact_relationship"
                value={formData.emergency_contact_relationship}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="emergency_contact_phone">Emergency Contact Phone</label>
              <input
                type="tel"
                id="emergency_contact_phone"
                name="emergency_contact_phone"
                value={formData.emergency_contact_phone}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Physician Information Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Primary Physician Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="primary_physician_name">Primary Physician Name</label>
              <input
                type="text"
                id="primary_physician_name"
                name="primary_physician_name"
                value={formData.primary_physician_name}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="primary_physician_phone">Primary Physician Phone</label>
              <input
                type="tel"
                id="primary_physician_phone"
                name="primary_physician_phone"
                value={formData.primary_physician_phone}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="primary_physician_address">Primary Physician Address</label>
              <textarea
                id="primary_physician_address"
                name="primary_physician_address"
                value={formData.primary_physician_address}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            {/* Health Conditions Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Health Conditions</h3>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="health_condition">Health Condition</label>
              <textarea
                id="health_condition"
                name="health_condition"
                value={formData.health_condition}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="allergies">Allergies</label>
              <textarea
                id="allergies"
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="medication_list">Medication List</label>
              <textarea
                id="medication_list"
                name="medication_list"
                value={formData.medication_list}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="dietary_restrictions">Dietary Restrictions</label>
              <textarea
                id="dietary_restrictions"
                name="dietary_restrictions"
                value={formData.dietary_restrictions}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="meal_alert">Meal Alert</label>
              <textarea
                id="meal_alert"
                name="meal_alert"
                value={formData.meal_alert}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            {/* Medical History Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Medical History</h3>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="hospital_history">Hospital History</label>
              <textarea
                id="hospital_history"
                name="hospital_history"
                value={formData.hospital_history}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="physical_limitation">Physical Limitation</label>
              <textarea
                id="physical_limitation"
                name="physical_limitation"
                value={formData.physical_limitation}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="mental_health_issue">Mental Health Issue</label>
              <textarea
                id="mental_health_issue"
                name="mental_health_issue"
                value={formData.mental_health_issue}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            {/* Care Requirements Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Care Requirements</h3>
            </div>

            <div className="form-group">
              <label htmlFor="level_of_care_needed">Level of Care Needed</label>
              <select
                id="level_of_care_needed"
                name="level_of_care_needed"
                value={formData.level_of_care_needed}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select...</option>
                <option value="Basic">Basic</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Complex">Complex</option>
              </select>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="daily_care_activities">Daily Care Activities</label>
              <textarea
                id="daily_care_activities"
                name="daily_care_activities"
                value={formData.daily_care_activities}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="special_care_instructions">Special Care Instructions</label>
              <textarea
                id="special_care_instructions"
                name="special_care_instructions"
                value={formData.special_care_instructions}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="schedule_psw_visit">Schedule PSW Visit</label>
              <textarea
                id="schedule_psw_visit"
                name="schedule_psw_visit"
                value={formData.schedule_psw_visit}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            {/* Emergency & Preferences Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Emergency & Preferences</h3>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="emergency_procedures">Emergency Procedures</label>
              <textarea
                id="emergency_procedures"
                name="emergency_procedures"
                value={formData.emergency_procedures}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="known_triggers_for_medical_emergencies">Known Triggers for Medical Emergencies</label>
              <textarea
                id="known_triggers_for_medical_emergencies"
                name="known_triggers_for_medical_emergencies"
                value={formData.known_triggers_for_medical_emergencies}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="client_preferred_communication_method">Preferred Communication Method</label>
              <input
                type="text"
                id="client_preferred_communication_method"
                name="client_preferred_communication_method"
                value={formData.client_preferred_communication_method}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="cultural_religious_preferences">Cultural/Religious Preferences</label>
              <textarea
                id="cultural_religious_preferences"
                name="cultural_religious_preferences"
                value={formData.cultural_religious_preferences}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            {/* PSW & Service Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>PSW & Service Details</h3>
            </div>

            <div className="form-group">
              <label htmlFor="preferred_psw">Preferred PSW</label>
              <input
                type="text"
                id="preferred_psw"
                name="preferred_psw"
                value={formData.preferred_psw}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="caregiver_assigned">Caregiver Assigned</label>
              <input
                type="text"
                id="caregiver_assigned"
                name="caregiver_assigned"
                value={formData.caregiver_assigned}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="service_start_date">Service Start Date</label>
              <input
                type="date"
                id="service_start_date"
                name="service_start_date"
                value={formData.service_start_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="service_end_date">Service End Date</label>
              <input
                type="date"
                id="service_end_date"
                name="service_end_date"
                value={formData.service_end_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Billing Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Billing Information</h3>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="billing_address">Billing Address</label>
              <textarea
                id="billing_address"
                name="billing_address"
                value={formData.billing_address}
                onChange={handleChange}
                className="form-textarea"
                rows="2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="payment_plan">Payment Plan</label>
              <input
                type="text"
                id="payment_plan"
                name="payment_plan"
                value={formData.payment_plan}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="payment_due_date">Payment Due Date</label>
              <input
                type="date"
                id="payment_due_date"
                name="payment_due_date"
                value={formData.payment_due_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="billing_contact_name">Billing Contact Name</label>
              <input
                type="text"
                id="billing_contact_name"
                name="billing_contact_name"
                value={formData.billing_contact_name}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="billing_contact_phone">Billing Contact Phone</label>
              <input
                type="tel"
                id="billing_contact_phone"
                name="billing_contact_phone"
                value={formData.billing_contact_phone}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Additional Notes Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Additional Notes</h3>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-textarea"
                rows="4"
                placeholder="Any additional notes..."
              />
            </div>
          </div>

          <div className="modal-actions" style={{ marginTop: '20px' }}>
            <button
              type="button"
              className="modal-button-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-button-submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Information'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserAdditionalInfoModal;
